import { createApi } from '@reduxjs/toolkit/query/react';
import { formatTagText } from 'components/tables/OrdersTable/ordersTable.settings';
import { PatientsQueryParams } from 'models/patients.types';
import { baseQueryWithReauth } from 'utils/planex';

import {
  NovoEntitlement,
  OrderMedicationOption,
  OrderProps,
  OrderStatusOption,
  OrderStatusResponse,
  OrderTagOption,
  OrderTagResponse,
  OrderTypeOption,
  OrderTypeResponse,
  UpdateOrderRequestProps
} from './orders.types';

export const ordersApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    cancelOrder: builder.mutation({
      invalidatesTags: (_result, _error, arg) => [{ id: arg, type: 'Order' }],
      query: (orderId) => ({
        method: 'PUT',
        url: `${import.meta.env.VITE_API_URL}/planex/proxy/v2/orders/${orderId}/cancel`
      }),
      transformResponse: (response: { message: string }) => response
    }),
    createReplacement: builder.mutation<
      { data: { originalPlanexOrderId: string; planexOrderId: string } },
      {
        lineItemIds: string[];
        // (LMD-1427) Needed server-side to create the LMD-888 provider replacement-prescription
        // task — `lineItemIds` alone (pangea's own opaque ids) can't be resolved back to an
        // elationId/name on the backend. Optional and additive: see
        // create-replacement-order.schema.ts in the backend repo.
        medication?: { elationId: number; name: string };
        originalPlanexOrderId: string;
        prescribingProviderId?: string;
        reason: string;
      }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.originalPlanexOrderId, type: 'Order' }],
      query: ({
        lineItemIds,
        medication,
        originalPlanexOrderId,
        prescribingProviderId,
        reason
      }) => ({
        body: { lineItemIds, medication, originalPlanexOrderId, prescribingProviderId, reason },
        method: 'POST',
        url: `${import.meta.env.VITE_API_URL}/v2/replacement-orders`
      })
    }),
    exportOrders: builder.query<
      | { blob: Blob; count: number; type: 'csv' }
      | { count: number; limit: number; type: 'warning' },
      { confirmed?: boolean; params: PatientsQueryParams }
    >({
      query: ({ confirmed, params }) => ({
        params: confirmed ? { ...params, confirmed: 'true' } : params,
        responseHandler: async (response: Response) => {
          const contentType = response.headers.get('Content-Type') ?? '';
          if (contentType.includes('application/json')) {
            const json = await response.json();
            return { count: json.count, limit: json.limit, type: 'warning' as const };
          }
          const text = await response.text();
          const blob = new Blob([text], { type: 'text/csv' });
          // Subtract 1 for the header row; empty export = 0
          const count = text.trim() ? text.trim().split('\n').length - 1 : 0;
          return { blob, count, type: 'csv' as const };
        },
        url: '/v2/orders/export'
      })
    }),
    // (LMD-1942) The planex ledger entitlement of an order's fill. A panel action can touch other
    // orders' entitlements too (a replacement moved between entitlements), so every action
    // invalidates the whole 'NovoEntitlement' tag type.
    getNovoEntitlement: builder.query<NovoEntitlement, { orderId: string }>({
      providesTags: (_result, _error, arg) => [{ id: arg.orderId, type: 'NovoEntitlement' }],
      query: ({ orderId }) =>
        `${import.meta.env.VITE_API_URL}/planex/proxy/v2/orders/${orderId}/novo-entitlement`,
      transformResponse: (response: { data: NovoEntitlement }) => response.data
    }),
    getOrder: builder.query<OrderProps, { id: string }>({
      providesTags: (_result, _error, arg) => [{ id: arg.id, type: 'Order' }],
      query: ({ id }) => `/orders/${id}`,
      transformResponse: (response: { data: OrderProps }) => response.data
    }),

    getOrderMedications: builder.query<OrderMedicationOption[], void>({
      query: () => '/v2/orders/medications',
      transformResponse: (response: { data: OrderMedicationOption[] }) => response.data
    }),
    getOrders: builder.query<
      { orders: OrderProps[]; totalCount: number },
      { params: PatientsQueryParams }
    >({
      providesTags: (result) =>
        result
          ? ['Order', ...result.orders.map(({ _id }) => ({ id: _id, type: 'Order' as const }))]
          : ['Order'],
      query: ({ params }) => ({
        params,
        url: '/v2/orders'
      }),
      transformResponse: (response: { data: OrderProps[]; info: { totalCount: number } }) => ({
        orders: response.data,
        totalCount: response.info.totalCount
      })
    }),
    getOrderStatuses: builder.query<OrderStatusOption[], void>({
      query: () => '/order-statuses',
      transformResponse: (response: { data: OrderStatusResponse[] }) =>
        response.data
          .filter((status) => status.active)
          .map((status) => ({
            label: status.name,
            value: status.name
          }))
    }),
    getOrderTags: builder.query<OrderTagOption[], void>({
      query: () => '/order-tags',
      transformResponse: (response: { data: OrderTagResponse[] }) =>
        response.data
          .filter((tag) => tag.active)
          .map((tag) => ({
            label: formatTagText(tag.name) || tag.name || '-',
            value: tag.name
          }))
    }),

    getOrderTypes: builder.query<OrderTypeOption[], void>({
      query: () => '/order-types',
      transformResponse: (response: { data: OrderTypeResponse[] }) =>
        response.data
          .filter((type) => type.active)
          .map((type) => ({
            category: type.category,
            label: type.name,
            value: type.name
          }))
    }),
    markNovoReplacement: builder.mutation<
      { data: { result: string } },
      { orderId: string; originalOrderId: string; reason: string }
    >({
      invalidatesTags: (_result, _error, arg) => [
        'NovoEntitlement',
        { id: arg.orderId, type: 'Order' },
        { id: arg.originalOrderId, type: 'Order' }
      ],
      query: ({ orderId, originalOrderId, reason }) => ({
        body: { originalOrderId, reason },
        method: 'PUT',
        url: `${import.meta.env.VITE_API_URL}/planex/proxy/v2/orders/${orderId}/novo-entitlement/replacement`
      })
    }),
    recordNovoShipDate: builder.mutation<
      { data: { result: string } },
      { orderId: string; shippedAt: string }
    >({
      invalidatesTags: (_result, _error, arg) => [
        'NovoEntitlement',
        { id: arg.orderId, type: 'Order' }
      ],
      query: ({ orderId, shippedAt }) => ({
        body: { shippedAt },
        method: 'PUT',
        url: `${import.meta.env.VITE_API_URL}/planex/proxy/v2/orders/${orderId}/novo-entitlement/ship-date`
      })
    }),
    updateOrder: builder.mutation<{ message: string }, UpdateOrderRequestProps>({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.orderId, type: 'Order' }],
      query: ({ order, orderId }) => ({
        body: order,
        method: 'PUT',
        url: `${import.meta.env.VITE_API_URL}/planex/proxy/v2/orders/${orderId}`
      }),
      transformResponse: (response: { message: string }) => response
    }),
    voidNovoFill: builder.mutation<{ data: { result: string } }, { orderId: string }>({
      invalidatesTags: (_result, _error, arg) => [
        'NovoEntitlement',
        { id: arg.orderId, type: 'Order' }
      ],
      query: ({ orderId }) => ({
        method: 'PUT',
        url: `${import.meta.env.VITE_API_URL}/planex/proxy/v2/orders/${orderId}/novo-entitlement/void`
      })
    })
  }),
  reducerPath: 'planex',
  tagTypes: ['NovoEntitlement', 'Order']
});

export const {
  useCancelOrderMutation,
  useCreateReplacementMutation,
  useGetNovoEntitlementQuery,
  useGetOrderMedicationsQuery,
  useGetOrdersQuery,
  useGetOrderStatusesQuery,
  useGetOrderTagsQuery,
  useGetOrderTypesQuery,
  useLazyExportOrdersQuery,
  useLazyGetOrderQuery,
  useMarkNovoReplacementMutation,
  useRecordNovoShipDateMutation,
  useUpdateOrderMutation,
  useVoidNovoFillMutation
} = ordersApi;
